import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import {
  FlowDownstreamOwnersQueryVariables, FlowDownstreamOwnersQuery, OwnersDisplayGridRowDataFragment, FlowDownstreamOwnersFilteredByFlowOutputFieldQueryVariables, FlowDownstreamOwnersFilteredByFlowOutputFieldQuery, TableauUserSortOrder,
  FlowDownstreamOwnersFilteredByFlowOutputFieldDocument,
  FlowDownstreamOwnersDocument
} from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps
} from '../../../data-catalog-shared';
import { OwnersDisplayGrid, OwnersDisplayGridRowData } from '../sharedDisplayGrid/OwnersDisplayGrid';
import { OwnersValidSortFields } from '../sorting/sortProviders/OwnersGridSortProvider';

type QueryVariables = FlowDownstreamOwnersQueryVariables;
type QueryResults = FlowDownstreamOwnersQuery;
type QueryResultListNode = OwnersDisplayGridRowDataFragment;
type GridRowData = OwnersDisplayGridRowData;

type QueryVariablesWithFilter = FlowDownstreamOwnersFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamOwnersFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter = OwnersDisplayGridRowDataFragment;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamOwnersConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamOwnersConnection : null;
}

function mapResultNode(node: QueryResultListNode): GridRowData {
  return {
    // NOTE: Our selection model uses the `id`, but the "send email" API needs the users' VizPortal IDs.
    // So this ID is actually the vizportal ID. TFS 891208 will allow us to use any node property as the selection ID.
    // Using the vizportalId in the interim should work fine, however, as long as we're not querying across sites.
    id: node.vizportalId,
    name: node.name,
    username: node.username,
    domain: node.domain,
    workbookCount: node.ownedWorkbooksConnection ? node.ownedWorkbooksConnection.totalCount : null,
    datasourceCount: node.ownedDatasourcesConnection ? node.ownedDatasourcesConnection.totalCount : null,
    metricsCount: node.ownedMetricsConnection ? node.ownedMetricsConnection.totalCount : null,
    virtualConnectionsCount: node.ownedVirtualConnectionsConnection ? node.ownedVirtualConnectionsConnection.totalCount : null
  };
}

export type FlowDownstreamOwnersQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<OwnersValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamOwnersQueryGrid(props: FlowDownstreamOwnersQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamOwnersLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredOwnersLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with selection
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowDownstreamOwnersFilteredByFlowOutputFieldDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as TableauUserSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) => (
        <OwnersDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  ) : (
    // Query without selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FlowDownstreamOwnersDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        orderBy: props.orderBy as TableauUserSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) => (
        <OwnersDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  );
}
